// src/hooks/useListaTodos.jsx
import React from "react";
import {
  getDocs,
  addDoc,
  deleteDoc,
  collection,
  doc,
  query,
  where,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../firebaseConfig";

// ========== helpers puros (exportados para tests u otros componentes) ==========

// Normaliza espacios y mayúsculas
export const norm = (s = "") =>
  s.toString().trim().toUpperCase().replace(/\s+/g, " ");

// Fusión de registros priorizando no-vacíos y textos más largos
export const mergeRecords = (a = {}, b = {}) => {
  const pick = (k) => {
    const va = (a[k] ?? "").toString().trim();
    const vb = (b[k] ?? "").toString().trim();
    if (!va && !vb) return "";
    if (va && !vb) return va;
    if (!va && vb) return vb;
    return va.length >= vb.length ? va : vb;
  };
  return {
    empresa: pick("empresa"),
    rfc: pick("rfc"),
    regimenFiscal: pick("regimenFiscal"),
    moneda: pick("moneda"),
    telefono: pick("telefono"),
    correo: pick("correo"),
    condicionesPago: pick("condicionesPago"),
    fechaAlta: pick("fechaAlta"),          
    imagenURL: pick("imagenURL"),
    domicilio: pick("domicilio"),
    numeroExt: pick("numeroExt"),
    numeroInt: pick("numeroInt"),
    colonia: pick("colonia"),
    codigoPostal: pick("codigoPostal"),
    ciudad: pick("ciudad"),
    estado: pick("estado"),
  };
};

export const normRFC = (s = "") =>
  s
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
export const normEmpresaClave = (s = "") => {
  const base = s
    .toUpperCase()
    .replace(/\bS\.?\s*A\.?\s*DE\s*C\.?\s*V\.?\b/g, "")
    .replace(/\bS\.?\s*DE\s*R\.?\s*L\.?(?:\s*DE\s*C\.?\s*V\.?)?\b/g, "")
    .replace(/\bSOCIEDAD ANONIMA\b/g, "")
    .replace(/\bSOCIEDAD ANÓNIMA\b/g, "")
    .replace(/[.,]/g, " ");
  return norm(base);
};

export const tipo2pref = (t) =>
  t === "Cliente" ? "C" : t === "Proveedor" ? "P" : "A";

export const normalizeManualId = (tipo, raw) => {
  const prefix = tipo2pref(tipo);
  const val = (raw || "").toString().trim().toUpperCase();
  if (!val) return "";
  if (/^\d+$/.test(val)) return `${prefix}-${val.padStart(4, "0")}`;
  const m = val.match(/(\d+)/);
  if (val.startsWith(prefix + "-") && m)
    return `${prefix}-${String(m[1]).padStart(4, "0")}`;
  if (m) return `${prefix}-${String(m[1]).padStart(4, "0")}`;
  return val;
};

export const getNumericPart = (id) => {
  if (!id) return null;
  const m = String(id)
    .trim()
    .toUpperCase()
    .match(/^[CPA]-(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
};

export const existeId = async (id, ignoreDocId = null) => {
  const idU = String(id || "").toUpperCase();
  const [qc, qp] = await Promise.all([
    getDocs(query(collection(db, "clientes"), where("id", "==", idU))),
    getDocs(query(collection(db, "proveedores"), where("id", "==", idU))),
  ]);
  return [...qc.docs, ...qp.docs].some((d) => d.id !== ignoreDocId);
};

export const generarIdLibre = async (tipo, ignoreDocId = null) => {
  const pref = tipo2pref(tipo);
  const [snapCli, snapProv] = await Promise.all([
    getDocs(collection(db, "clientes")),
    getDocs(collection(db, "proveedores")),
  ]);

  let max = 0;
  const considerar = (d) => {
    if (ignoreDocId && d.id === ignoreDocId) return;
    const v = String(d.data()?.id || "").toUpperCase();
    const m = v.match(/^([CPA])-(\d+)$/);
    if (m && m[1] === pref) {
      const n = parseInt(m[2], 10);
      if (!Number.isNaN(n) && n > max) max = n;
    }
  };

  snapCli.docs.forEach(considerar);
  snapProv.docs.forEach(considerar);

  const siguiente = max + 1;
  return `${pref}-${String(siguiente).padStart(4, "0")}`;
};

// Renumera TODAS las series con batch (C, A, P) manteniendo orden por clave robusta
export const renumerarIdsConsecutivos = async () => {
  const [snapCli, snapProv] = await Promise.all([
    getDocs(collection(db, "clientes")),
    getDocs(collection(db, "proveedores")),
  ]);

  const docsC = [];
  const docsA = [];
  const docsP = [];

  snapCli.docs.forEach((d) => {
    const data = d.data() || {};
    if (data.tipo === "Cliente")
      docsC.push({ coll: "clientes", docId: d.id, data });
    else if (data.tipo === "Ambos")
      docsA.push({ coll: "clientes", docId: d.id, data });
  });
  snapProv.docs.forEach((d) => {
    const data = d.data() || {};
    if (data.tipo === "Proveedor")
      docsP.push({ coll: "proveedores", docId: d.id, data });
  });

  const keyOf = (x) =>
    normRFC(x.data.rfc || "") || normEmpresaClave(x.data.empresa || "");
  const sortByKey = (arr) =>
    arr.sort((a, b) =>
      keyOf(a) < keyOf(b) ? -1 : keyOf(a) > keyOf(b) ? 1 : 0
    );
  sortByKey(docsC);
  sortByKey(docsA);
  sortByKey(docsP);

  const batch = writeBatch(db);
  const renum = (arr, pref) => {
    for (let i = 0; i < arr.length; i++) {
      const wanted = `${pref}-${String(i + 1).padStart(4, "0")}`;
      const current = (arr[i].data.id || "").toUpperCase();
      if (current !== wanted) {
        batch.set(
          doc(db, arr[i].coll, arr[i].docId),
          { ...arr[i].data, id: wanted },
          { merge: true }
        );
      }
    }
  };

  renum(docsC, "C");
  renum(docsA, "A");
  renum(docsP, "P");

  await batch.commit();
};

// ========== hook ==========

const DEFAULT_FORM = {
  id: "",
  tipo: "Cliente",
  empresa: "",
  rfc: "",
  regimenFiscal: "",
  moneda: "",
  telefono: "",
  correo: "",
  condicionesPago: "",  
  fechaAlta: "",        
  imagenURL: null,
  domicilio: "",
  numeroExt: "",
  numeroInt: "",
  colonia: "",
  codigoPostal: "",
  ciudad: "",
  estado: "",
};

// debounce simple
const useDebouncedValue = (value, ms = 250) => {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

export function useListaTodos({ modoNumeroContacto }) {
  // estado principal
  const [clientes, setClientes] = React.useState([]);
  const [proveedores, setProveedores] = React.useState([]);
  const [ambos, setAmbos] = React.useState([]);

  const [formData, setFormData] = React.useState(DEFAULT_FORM);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [editingRef, setEditingRef] = React.useState(null); // { coll: 'clientes'|'proveedores', docId } | null

  const [searchQuery, setSearchQuery] = React.useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 200);
  const [sortOrder, setSortOrder] = React.useState("asc");
  const [sortBy, setSortBy] = React.useState("id");

  const [loadedCli, setLoadedCli] = React.useState(false);
  const [loadedProv, setLoadedProv] = React.useState(false);
  const isLoading = !(loadedCli && loadedProv);

  // ---- normalización y deduplicación (batch) ----
  const normalizeAndDedupContacts = React.useCallback(async () => {
    const [snapCli, snapProv] = await Promise.all([
      getDocs(collection(db, "clientes")),
      getDocs(collection(db, "proveedores")),
    ]);

    const rawCli = snapCli.docs.map((d) => ({
      docId: d.id,
      coll: "clientes",
      ...d.data(),
    }));
    const rawProv = snapProv.docs.map((d) => ({
      docId: d.id,
      coll: "proveedores",
      ...d.data(),
    }));

    const keyOf = (x) =>
      normRFC(x.rfc || "") || normEmpresaClave(x.empresa || "");
    const groups = new Map();
    const addTo = (x, bucket) => {
      const k = keyOf(x);
      if (!k) return;
      if (!groups.has(k)) groups.set(k, { cli: [], prov: [] });
      groups.get(k)[bucket].push(x);
    };
    rawCli.forEach((x) => addTo(x, "cli"));
    rawProv.forEach((x) => addTo(x, "prov"));

    const batch = writeBatch(db);
    const hasPrefix = (id, t) => {
      if (!id) return false;
      const p = tipo2pref(t);
      return new RegExp(`^${p}-\\d+$`, "i").test(String(id));
    };

    for (const [, { cli, prov }] of groups) {
      const typesCli = new Set(cli.map((x) => (x.tipo || "").toString()));
      const typesProv = new Set(prov.map((x) => (x.tipo || "").toString()));

      let finalType;
      if (
        typesCli.has("Ambos") ||
        typesProv.has("Ambos") ||
        (cli.length > 0 && prov.length > 0)
      ) {
        finalType = "Ambos";
      } else if (prov.length > 0 && cli.length === 0) {
        finalType = "Proveedor";
      } else {
        finalType = "Cliente";
      }

      const all = [...cli, ...prov];
      let fused = {};
      all.forEach((x) => {
        fused = mergeRecords(fused, x);
      });

      let finalId = "";
      const withRightPrefix = all
        .map((x) => String(x.id || "").toUpperCase())
        .find((id) => hasPrefix(id, finalType));
      if (withRightPrefix) finalId = withRightPrefix;
      else finalId = await generarIdLibre(finalType);

      const payload = { ...fused, tipo: finalType, id: finalId };

      const targetColl = finalType === "Proveedor" ? "proveedores" : "clientes";
      const existingInTarget = targetColl === "clientes" ? cli[0] : prov[0];

      if (existingInTarget) {
        batch.set(doc(db, targetColl, existingInTarget.docId), payload, {
          merge: true,
        });
      } else {
        // crear nuevo doc con id autogenerado
        // (en batch no hay addDoc; workaround: pre-crear ref)
        const newRef = doc(collection(db, targetColl));
        batch.set(newRef, payload);
      }

      const toKeepDocId = existingInTarget?.docId || null;
      all.forEach((x) => {
        if (x.docId !== toKeepDocId || x.coll !== targetColl) {
          batch.delete(doc(db, x.coll, x.docId));
        }
      });
    }

    await batch.commit();
  }, []);

  // snapshots (viven "Ambos" en clientes)
  React.useEffect(() => {
    const unsubCli = onSnapshot(collection(db, "clientes"), (snap) => {
      const lista = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setClientes(lista.filter((d) => d.tipo === "Cliente"));
      setAmbos(lista.filter((d) => d.tipo === "Ambos"));
      setLoadedCli(true);
    });

    const unsubProv = onSnapshot(collection(db, "proveedores"), (snap) => {
      const lista = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setProveedores(lista.filter((d) => d.tipo === "Proveedor"));
      setLoadedProv(true);
    });

    // normaliza una sola vez al montar
    (async () => {
      try {
        await normalizeAndDedupContacts();
      } catch {
        /* noop */
      }
    })();

    return () => {
      unsubCli();
      unsubProv();
    };
  }, [normalizeAndDedupContacts]);

  // ====== UI helpers ======
  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortOrder("asc");
      return column;
    });
  };

  const resetFormData = () => setFormData(DEFAULT_FORM);

  const handleOpenModal = async () => {
    resetFormData();
    setEditingRef(null);
    const nextId = await generarIdLibre("Cliente");
    const todayStr = new Date().toISOString().slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      tipo: "Cliente",
      id: nextId,
      fechaAlta: todayStr,
    }));
    setStep(1);
    setModalIsOpen(true);
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
 
    // Cambio de tipo: conserva el número en edición, autogenera en alta
    if (name === "tipo") {
      const nextTipo = value;
 
      if (editingRef) {
        setFormData((prev) => {
          const n = getNumericPart(prev.id);
          const keepId =
            n != null
              ? `${tipo2pref(nextTipo)}-${String(n).padStart(4, "0")}`
              : prev.id;
          return { ...prev, tipo: nextTipo, id: keepId };
        });
      } else {
        const nextId = await generarIdLibre(nextTipo);
        setFormData((prev) => ({ ...prev, tipo: nextTipo, id: nextId }));
      }
      return;
    }
 
    // Condiciones de pago: “+ Agregar…”
    if (name === "condicionesPago") {
      if (value === "__add__") {
        const { isConfirmed, value: personalizada } = await Swal.fire({
          title: "Agregar condición de pago",
          input: "text",
          inputLabel: "Escribe la condición",
          inputPlaceholder: "Ej. Crédito 21 días",
          showCancelButton: true,
          confirmButtonText: "Guardar",
          cancelButtonText: "Cancelar",
          inputValidator: (v) => {
            if (!v || !v.trim()) return "Escribe una condición válida.";
            if (v.length > 60) return "Máximo 60 caracteres.";
            return undefined;
          },
        });
 
        if (isConfirmed && personalizada?.trim()) {
          setFormData((prev) => ({
            ...prev,
            condicionesPago: personalizada.trim(),
          }));
        }
        // Importante: no dejes el select en "__add__"
        return;
      }
 
      setFormData((prev) => ({ ...prev, condicionesPago: value }));
      return;
    }
 
    // Imagen
    if (name === "imagen" && files?.length > 0) {
      const image = files[0];
      setFormData((prev) => ({
        ...prev,
        imagenURL: URL.createObjectURL(image),
      }));
      return;
    }
 
    // Resto de campos (incluye fechaAlta)
    setFormData((prev) => ({ ...prev, [name]: value }));
  };  

  const filteredContacts = React.useMemo(() => {
    const allContacts = [...clientes, ...proveedores, ...ambos];
    if (!allContacts.length) return [];
    const q = debouncedSearch.toLowerCase();
    if (!q) return allContacts;
    return allContacts.filter((c) => {
      const fields = [
        c.id?.toString().toLowerCase() || "",
        c.tipo?.toString().toLowerCase() || "",
        c.empresa?.toString().toLowerCase() || "",
        c.rfc?.toString().toLowerCase() || "",
        c.telefono?.toString().toLowerCase() || "",
        c.correo?.toString().toLowerCase() || "",
        c.condicionesPago?.toString().toLowerCase() || "",
        c.fechaAlta?.toString().toLowerCase() || "",      
      ];
      return fields.some((f) => f.includes(q));
    });
  }, [clientes, proveedores, ambos, debouncedSearch]);

  const sortedContacts = React.useMemo(() => {
    const data = [...filteredContacts];
    const compareIds = (a, b) => {
      const ida = (a.id || "").toUpperCase();
      const idb = (b.id || "").toUpperCase();
      const pa = ida[0] === "C" ? "C" : ida[0] === "P" ? "P" : "A";
      const pb = idb[0] === "C" ? "C" : idb[0] === "P" ? "P" : "A";
      const order = { C: 0, P: 1, A: 2 };
      if (order[pa] !== order[pb]) return order[pa] - order[pb];
      const na = getNumericPart(ida) ?? Number.MAX_SAFE_INTEGER;
      const nb = getNumericPart(idb) ?? Number.MAX_SAFE_INTEGER;
      return na - nb;
    };
    return data.sort((a, b) => {
      if (sortBy === "id") {
        const r = compareIds(a, b);
        return sortOrder === "asc" ? r : -r;
      }
      const A =
        typeof a[sortBy] === "string"
          ? a[sortBy].toLowerCase()
          : a[sortBy] || "";
      const B =
        typeof b[sortBy] === "string"
          ? b[sortBy].toLowerCase()
          : b[sortBy] || "";
      if (A < B) return sortOrder === "asc" ? -1 : 1;
      if (A > B) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredContacts, sortBy, sortOrder]);

  const handleEdit = (docId, tipo) => {
    if (tipo === "Ambos") {
      const c = ambos.find((x) => x.docId === docId);
      setFormData(c);
      setEditingRef({ coll: "clientes", docId }); // Ambos vive en clientes
    } else if (tipo === "Cliente") {
      const c = clientes.find((x) => x.docId === docId);
      setFormData(c);
      setEditingRef({ coll: "clientes", docId });
    } else {
      const p = proveedores.find((x) => x.docId === docId);
      setFormData(p);
      setEditingRef({ coll: "proveedores", docId });
    }
    setStep(1);
    setModalIsOpen(true);
  };

  const handleDelete = async (docId, tipo) => {
    const coll = tipo === "Proveedor" ? "proveedores" : "clientes"; // Ambos = clientes
    const noun =
      tipo === "Proveedor"
        ? "Proveedor"
        : tipo === "Cliente"
        ? "Cliente"
        : "Registro";
    const confirm = await Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás revertir esto!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
    });
    if (!confirm.isConfirmed) return;

    try {
      await deleteDoc(doc(db, coll, docId));
      await Swal.fire({
        icon: "success",
        title: `¡${noun} eliminado!`,
        showConfirmButton: false,
        timer: 900,
      });
    } catch (e) {
      console.error("Error al eliminar:", e);
    }
  };

  const handleSubmit = async (e, modoNumeroContactoLocal) => {
    e?.preventDefault?.();
 
    if (step === 1 || step === 2) {
      if (!formData.empresa?.trim()) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: 'El campo "Nombre de la Empresa" es obligatorio!',
        });
        return;
      }
    }
 
    try {
      let payload = {
        ...formData,
        telefono: formData.telefono || "",
        correo: formData.correo || "",
        rfc: formData.rfc || "",
        regimenFiscal: formData.regimenFiscal || "",
        condicionesPago: formData.condicionesPago || "",              
        fechaAlta: formData.fechaAlta || new Date().toISOString().slice(0,10),
      };
 
      const claveDe = (x) =>
        normRFC(x.rfc || "") || normEmpresaClave(x.empresa || "");
      const claveNueva = claveDe(payload);
 
      const yaClienteOA = [...clientes, ...ambos].some(
        (x) => claveDe(x) === claveNueva
      );
      const yaProveedor = proveedores.some((x) => claveDe(x) === claveNueva);
 
      const isEditing = Boolean(editingRef);
 
      // Al crear, si existe en el otro bucket, forzar "Ambos"
      if (!isEditing) {
        if (
          (payload.tipo === "Cliente" && yaProveedor) ||
          (payload.tipo === "Proveedor" && yaClienteOA)
        ) {
          payload.tipo = "Ambos";
        }
      }
 
      const ignoreDocId = isEditing ? editingRef.docId : null;
 
      // =========================
      // ID FINAL
      // =========================
      let finalId = "";
 
      if (isEditing) {
        // Mantener el mismo número y ajustar prefijo si cambió el tipo;
        // si el propuesto existe en otro doc, generar uno libre.
        const n = getNumericPart(formData.id);
        const proposed =
          n != null
            ? `${tipo2pref(payload.tipo)}-${String(n).padStart(4, "0")}`
            : normalizeManualId(payload.tipo, formData.id);
 
        if (proposed) {
          if (await existeId(proposed, ignoreDocId)) {
            // choque → asigna uno libre del mismo tipo
            finalId = await generarIdLibre(payload.tipo, ignoreDocId);
          } else {
            finalId = proposed.toUpperCase();
          }
        } else {
          // no se pudo proponer → asigna uno libre
          finalId = await generarIdLibre(payload.tipo, ignoreDocId);
        }
      } else {
        // ALTAS NUEVAS: respeta modo (auto/manual) y valida duplicados
        if (modoNumeroContactoLocal === "auto") {
          finalId = await generarIdLibre(payload.tipo);
        } else {
          const proposed = normalizeManualId(payload.tipo, payload.id);
          if (!proposed) {
            finalId = await generarIdLibre(payload.tipo);
          } else if (await existeId(proposed)) {
            return Swal.fire({
              icon: "error",
              title: "ID duplicado",
              text: `El ID ${proposed} ya existe.`,
            });
          } else {
            finalId = proposed.toUpperCase();
          }
        }
      }
 
      payload.id = finalId;
 
      const targetCollection =
        payload.tipo === "Proveedor" ? "proveedores" : "clientes";
 
      if (!isEditing) {
        if (payload.tipo === "Cliente" && yaClienteOA)
          return Swal.fire({
            icon: "error",
            title: "Duplicado",
            text: "Esta empresa ya existe como Cliente/Ambos.",
          });
        if (payload.tipo === "Proveedor" && yaProveedor)
          return Swal.fire({
            icon: "error",
            title: "Duplicado",
            text: "Esta empresa ya existe como Proveedor.",
          });
        if (payload.tipo === "Ambos" && (yaClienteOA || yaProveedor))
          return Swal.fire({
            icon: "error",
            title: "Duplicado",
            text: "Esta empresa ya existe. Edítala.",
          });
 
        await addDoc(collection(db, targetCollection), payload);
        const noun =
          payload.tipo === "Proveedor"
            ? "Proveedor"
            : payload.tipo === "Cliente"
            ? "Cliente"
            : "Contacto";
        await Swal.fire({
          icon: "success",
          title: `¡${noun} registrado!`,
          text: `ID asignado: ${payload.id}`,
          showConfirmButton: false,
          timer: 1200,
        });
      } else {
        // Actualizar / mover atómicamente conservando el mismo docId
        const batch = writeBatch(db);
        const originalCollection = editingRef.coll;
        const originalDocId = editingRef.docId;
        const noun =
          payload.tipo === "Proveedor"
            ? "Proveedor"
            : payload.tipo === "Cliente"
            ? "Cliente"
            : "Contacto";
 
        if (originalCollection === targetCollection) {
          batch.update(doc(db, targetCollection, originalDocId), payload);
        } else {
          batch.set(doc(db, targetCollection, originalDocId), payload, {
            merge: true,
          });
          batch.delete(doc(db, originalCollection, originalDocId));
        }
        await batch.commit();
 
        await Swal.fire({
          icon: "success",
          title: `¡${noun} actualizado!`,
          showConfirmButton: false,
          timer: 1000,
        });
      }
 
      // reset
      resetFormData();
      setEditingRef(null);
      setStep(1);
      setModalIsOpen(false);
 
      // normalización (no bloquea la UI)
      normalizeAndDedupContacts();
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al guardar. Intenta nuevamente.",
      });
    }
  };  

  return {
    // estado/base
    clientes,
    proveedores,
    ambos,
    isLoading,
    // formulario y modal
    formData,
    setFormData,
    modalIsOpen,
    setModalIsOpen,
    step,
    setStep,
    editingRef,
    // búsqueda/orden
    searchQuery,
    setSearchQuery,
    sortBy,
    sortOrder,
    handleSort,
    // listas derivadas
    filteredContacts,
    sortedContacts,
    // acciones
    handleOpenModal,
    handleChange,
    handleEdit,
    handleDelete,
    handleSubmit,
    normalizeAndDedupContacts,
  };
}
